import { CALCULATION_RESULT_TEMPLATE, STATE_TEMPLATE, STATUS_TEMPLATE } from '../../../../config/templates';
import { field } from '../../../../field-types';
import { defineRepresentation, RepresentationCategory } from '../../../../ontology.model';

// ------------------------------------------------
// MODEL DEFINITIONS
// ------------------------------------------------
// These define the type-safe structure of each representation.
// The `as const` ensures TypeScript preserves the literal types.

const GeometricParamsModel = {
  a: field.number({
    label: 'a',
    label_type: 'latex',
    description: 'The first term of the sequence ($u_1$)',
    example: 3,
  }),
  r: field.number({
    label: 'r',
    label_type: 'latex',
    description: 'The common ratio between consecutive terms ($r = u_{n+1} / u_n$)',
    example: 0.5,
  }),
  n: field.natural({
    label: 'n',
    label_type: 'latex',
    description: 'The position of the term ($n \\geq 1$)',
    example: 10,
  }),
} as const;

const NthTermModel = {
  value: field.number({
    label: 'u_n',
    label_type: 'latex',
    description: 'The value of the $n$-th term',
    example: 0.005859375,
  }),
  notation: field.latex({
    description: 'The algebraic notation (e.g. $u_{10}$)',
    example: 'u_{10}',
    optional: true,
  }),
  expression: field.latex({
    description: 'The mathematical expression with substituted values',
    example: '3(0.5)^9',
    optional: true,
  }),
} as const;

const PartialSumModel = {
  value: field.number({
    label: 'S_n',
    label_type: 'latex',
    description: 'The sum of the first $n$ terms',
    example: 5.994,
  }),
  notation: field.latex({
    description: 'The algebraic notation (e.g. $S_{10}$)',
    example: 'S_{10}',
    optional: true,
  }),
  expression: field.latex({
    description: 'The mathematical expression with substituted values',
    example: '3\\frac{1-0.5^{10}}{1-0.5}',
    optional: true,
  }),
} as const;

const InfiniteSumModel = {
  value: field.number({
    label: 'S_\\infty',
    label_type: 'latex',
    description: 'The sum of the infinite series',
    example: 6,
  }),
  notation: field.latex({
    description: 'The algebraic notation ($S_\\infty$)',
    example: 'S_{∞}',
    optional: true,
  }),
  expression: field.latex({
    description: 'The mathematical expression with substituted values',
    example: '\\frac{3}{1-0.5}',
    optional: true,
  }),
} as const;

const SequenceBehaviorModel = {
  state: field.enum(['INCREASING', 'DECREASING', 'ALTERNATING', 'CONSTANT'] as const, {
    description: 'The growth pattern of the sequence terms',
    example: 'DECREASING',
  }),
  description: field.string({
    description: 'A human-readable explanation of the behavior',
    example: 'Terms approach zero',
    optional: true,
  }),
} as const;

const ConvergenceStatusModel = {
  status: field.enum(['CONVERGENT', 'DIVERGENT'] as const, {
    description: 'Whether the infinite series has a finite sum (requires $|r| < 1$)',
    example: 'CONVERGENT',
  }),
} as const;

// ------------------------------------------------
// REPRESENTATIONS
// ------------------------------------------------

export const rep = {
  params: defineRepresentation({
    id: 'rep.geo.params',
    name: 'Sequence Definition',
    tag_ids: ['tag.geo'],
    description:
      'The defining parameters of a geometric sequence: first term $a$, common ratio $r$, and term position $n$.',
    category: RepresentationCategory.Object,
    type: 'numerical',
    model: GeometricParamsModel,
    template: 'a={{a}}, r={{r}} (n={{n}})',
    example_data: { a: 3, r: 0.5, n: 10 },
  }),

  term_result: defineRepresentation({
    id: 'rep.geo.term_result',
    name: 'N-th Term',
    tag_ids: ['tag.geo.seq'],
    description: 'The computed value of the $n$-th term in the sequence ($u_n = a \\cdot r^{n-1}$).',
    category: RepresentationCategory.Value,
    type: 'numerical',
    model: NthTermModel,
    template: CALCULATION_RESULT_TEMPLATE,
    example_data: {
      value: 0.005859375,
      notation: 'u_{10}',
      expression: '3(0.5)^9',
    },
  }),

  finite_sum_result: defineRepresentation({
    id: 'rep.geo.finite_sum_result',
    name: 'Partial Sum',
    tag_ids: ['tag.geo.series'],
    description: 'The sum of the first $n$ terms of the sequence ($S_n$).',
    category: RepresentationCategory.Value,
    type: 'numerical',
    model: PartialSumModel,
    template: CALCULATION_RESULT_TEMPLATE,
    example_data: {
      value: 5.994,
      notation: 'S_{10}',
      expression: '3\\frac{1-0.5^{10}}{1-0.5}',
    },
  }),

  sum_result: defineRepresentation({
    id: 'rep.geo.sum_result',
    name: 'Infinite Sum',
    tag_ids: ['tag.geo.series'],
    description: 'The sum of the infinite geometric series ($S_\\infty$), valid only when $|r| < 1$.',
    category: RepresentationCategory.Value,
    type: 'numerical',
    model: InfiniteSumModel,
    template: CALCULATION_RESULT_TEMPLATE,
    example_data: {
      value: 6,
      notation: 'S_{∞}',
      expression: '\\frac{3}{1-0.5}',
    },
  }),

  behavior: defineRepresentation({
    id: 'rep.geo.behavior',
    name: 'Monotonicity',
    tag_ids: ['tag.geo.seq'],
    description:
      'The growth pattern of the sequence: whether terms are increasing, decreasing, alternating, or constant.',
    category: RepresentationCategory.State,
    type: 'state',
    model: SequenceBehaviorModel,
    template: STATE_TEMPLATE,
    example_data: { state: 'DECREASING', description: 'Terms approach zero.' },
  }),

  convergence: defineRepresentation({
    id: 'rep.geo.convergence',
    name: 'Convergence',
    tag_ids: ['tag.geo.series'],
    description: 'Whether the infinite series converges to a finite sum (requires $|r| < 1$).',
    category: RepresentationCategory.State,
    type: 'state',
    model: ConvergenceStatusModel,
    template: STATUS_TEMPLATE,
    example_data: { status: 'CONVERGENT' },
  }),
};

// Export as array for the compiler
export const geometricRepresentations = Object.values(rep);

// ------------------------------------------------
// MODEL REGISTRY
// ------------------------------------------------
// This maps representation IDs to their models for type-safe procedures.

export const models = {
  'rep.geo.params': GeometricParamsModel,
  'rep.geo.term_result': NthTermModel,
  'rep.geo.finite_sum_result': PartialSumModel,
  'rep.geo.sum_result': InfiniteSumModel,
  'rep.geo.behavior': SequenceBehaviorModel,
  'rep.geo.convergence': ConvergenceStatusModel,
} as const;
